import type { LevelConfig } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as
// block_showcase.ts's local PIECE map (keeps debug-only code off the real
// dialog import chain).
const COMMON_DIFFICULTY = 0;

/**
 * Static test level for the Cube.rotationDegX/Y/Z fields (see level_config.ts).
 * Two depth layers (layers[0] and layers[1] sit at different world Z, see
 * GameScreen#createLayeredBlocks) — layer_index 0 renders FARTHER from the
 * camera and later indices closer (confirmed by hand: putting the columns
 * first hid them behind the wall), so the back wall has to come first here:
 *   - back (layers[0]): a solid stone wall with a row of Y-rotated cubes
 *     across the top. Made noticeably taller (6 rows) than the front columns
 *     (5 rows) on purpose — same width, so with both auto-centred it's the
 *     only way for the wall to stay visible above the columns instead of
 *     being fully hidden behind them (confirmed by hand).
 *   - front (layers[1]): four stone-base columns, each topped with an
 *     X-rotated cube, a jar on top of that, and a Z-rotated ("diamond")
 *     cube on top of the jar.
 * Rotated pieces need at least one empty grid cell between neighbours on the
 * same row (confirmed by hand) — their diagonal footprint is wider than
 * their 1x1 grid cell, so placed edge-to-edge they visually clip into
 * each other.
 */
const DIAMOND_TEST_LEVEL: LevelConfig = {
  level: 90300,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  tiltDeg: 6,
  layers: [
    // Back: a solid stone wall with a row of Y-rotated cubes across the top.
    {
      grid: [
        [ 1, 0, 2, 0, 3, 0, 4 ],
        [ 5, 6, 7, 8, 9, 10, 11 ],
        [ 12, 13, 14, 15, 16, 17, 18 ],
        [ 19, 20, 21, 22, 23, 24, 25 ],
        [ 26, 27, 28, 29, 30, 31, 32 ],
        [ 33, 34, 35, 36, 37, 38, 39 ],
        [ 40, 41, 42, 43, 44, 45, 46 ],
      ],
      pieces: [
        // X-rotated cubes across the top of the wall — the piece
        // lockRotations() (see breakable_physics_entity.ts) is meant to fix.
        { id: 1, type: 1, x: 0, y: 0, width: 1, height: 1, rotationDegX: 45 },
        { id: 2, type: 1, x: 2, y: 0, width: 1, height: 1, rotationDegX: 45 },
        { id: 3, type: 1, x: 4, y: 0, width: 1, height: 1, rotationDegX: 45 },
        { id: 4, type: 1, x: 6, y: 0, width: 1, height: 1, rotationDegX: 45 },
        // Solid stone wall, 7 wide x 6 tall.
        { id: 5, type: 2, x: 0, y: 1, width: 1, height: 1 },
        { id: 6, type: 2, x: 1, y: 1, width: 1, height: 1 },
        { id: 7, type: 2, x: 2, y: 1, width: 1, height: 1 },
        { id: 8, type: 2, x: 3, y: 1, width: 1, height: 1 },
        { id: 9, type: 2, x: 4, y: 1, width: 1, height: 1 },
        { id: 10, type: 2, x: 5, y: 1, width: 1, height: 1 },
        { id: 11, type: 2, x: 6, y: 1, width: 1, height: 1 },
        { id: 12, type: 2, x: 0, y: 2, width: 1, height: 1 },
        { id: 13, type: 2, x: 1, y: 2, width: 1, height: 1 },
        { id: 14, type: 2, x: 2, y: 2, width: 1, height: 1 },
        { id: 15, type: 2, x: 3, y: 2, width: 1, height: 1 },
        { id: 16, type: 2, x: 4, y: 2, width: 1, height: 1 },
        { id: 17, type: 2, x: 5, y: 2, width: 1, height: 1 },
        { id: 18, type: 2, x: 6, y: 2, width: 1, height: 1 },
        { id: 19, type: 2, x: 0, y: 3, width: 1, height: 1 },
        { id: 20, type: 2, x: 1, y: 3, width: 1, height: 1 },
        { id: 21, type: 2, x: 2, y: 3, width: 1, height: 1 },
        { id: 22, type: 2, x: 3, y: 3, width: 1, height: 1 },
        { id: 23, type: 2, x: 4, y: 3, width: 1, height: 1 },
        { id: 24, type: 2, x: 5, y: 3, width: 1, height: 1 },
        { id: 25, type: 2, x: 6, y: 3, width: 1, height: 1 },
        { id: 26, type: 2, x: 0, y: 4, width: 1, height: 1 },
        { id: 27, type: 2, x: 1, y: 4, width: 1, height: 1 },
        { id: 28, type: 2, x: 2, y: 4, width: 1, height: 1 },
        { id: 29, type: 2, x: 3, y: 4, width: 1, height: 1 },
        { id: 30, type: 2, x: 4, y: 4, width: 1, height: 1 },
        { id: 31, type: 2, x: 5, y: 4, width: 1, height: 1 },
        { id: 32, type: 2, x: 6, y: 4, width: 1, height: 1 },
        { id: 33, type: 2, x: 0, y: 5, width: 1, height: 1 },
        { id: 34, type: 2, x: 1, y: 5, width: 1, height: 1 },
        { id: 35, type: 2, x: 2, y: 5, width: 1, height: 1 },
        { id: 36, type: 2, x: 3, y: 5, width: 1, height: 1 },
        { id: 37, type: 2, x: 4, y: 5, width: 1, height: 1 },
        { id: 38, type: 2, x: 5, y: 5, width: 1, height: 1 },
        { id: 39, type: 2, x: 6, y: 5, width: 1, height: 1 },
        { id: 40, type: 2, x: 0, y: 6, width: 1, height: 1 },
        { id: 41, type: 2, x: 1, y: 6, width: 1, height: 1 },
        { id: 42, type: 2, x: 2, y: 6, width: 1, height: 1 },
        { id: 43, type: 2, x: 3, y: 6, width: 1, height: 1 },
        { id: 44, type: 2, x: 4, y: 6, width: 1, height: 1 },
        { id: 45, type: 2, x: 5, y: 6, width: 1, height: 1 },
        { id: 46, type: 2, x: 6, y: 6, width: 1, height: 1 },
      ],
    },
    // Front: four columns, each a stack of [stone base] -> [X-rotated cube]
    // -> [jar] -> [Z-rotated ("diamond") cube].
    {
      grid: [
        [ 1, 0, 2, 0, 3, 0, 4 ],
        [ 5, 0, 6, 0, 7, 0, 8 ],
        [ 9, 0, 10, 0, 11, 0, 12 ],
        [ 13, 0, 14, 0, 15, 0, 16 ],
        [ 17, 0, 18, 0, 19, 0, 20 ],
      ],
      pieces: [
        // Diamonds (Z) on top of each column.
        { id: 1, type: 1, x: 0, y: 0, width: 1, height: 1, rotationDegZ: 45 },
        { id: 2, type: 1, x: 2, y: 0, width: 1, height: 1, rotationDegZ: 45 },
        { id: 3, type: 1, x: 4, y: 0, width: 1, height: 1, rotationDegZ: 45 },
        { id: 4, type: 1, x: 6, y: 0, width: 1, height: 1, rotationDegZ: 45 },
        // Jars underneath the diamonds.
        { id: 5, type: 3, x: 0, y: 1, width: 1, height: 1 },
        { id: 6, type: 3, x: 2, y: 1, width: 1, height: 1 },
        { id: 7, type: 3, x: 4, y: 1, width: 1, height: 1 },
        { id: 8, type: 3, x: 6, y: 1, width: 1, height: 1 },
        // X-rotated cubes under the jars — 45deg here is too steep for a jar
        // to stay put (a jar's own friction isn't boosted like a rotated
        // cube's is, and 45deg is right at the edge of what friction alone
        // can hold on a slope at all): the jars just slid straight off and
        // fell off the table (confirmed by hand). 20deg keeps a visible lean
        // without giving them a slope steep enough to slide down.
        { id: 9, type: 1, x: 0, y: 2, width: 1, height: 1, rotationDegY: 45 },
        { id: 10, type: 1, x: 2, y: 2, width: 1, height: 1, rotationDegY: 45 },
        { id: 11, type: 1, x: 4, y: 2, width: 1, height: 1, rotationDegY: 45 },
        { id: 12, type: 1, x: 6, y: 2, width: 1, height: 1, rotationDegY: 45 },
        // Stone column bases, two blocks tall.
        { id: 13, type: 2, x: 0, y: 3, width: 1, height: 1 },
        { id: 14, type: 2, x: 2, y: 3, width: 1, height: 1 },
        { id: 15, type: 2, x: 4, y: 3, width: 1, height: 1 },
        { id: 16, type: 2, x: 6, y: 3, width: 1, height: 1 },
        { id: 17, type: 2, x: 0, y: 4, width: 1, height: 1 },
        { id: 18, type: 2, x: 2, y: 4, width: 1, height: 1 },
        { id: 19, type: 2, x: 4, y: 4, width: 1, height: 1 },
        { id: 20, type: 2, x: 6, y: 4, width: 1, height: 1 },
      ],
    },
  ],
};

export class DiamondTowerShowcase
{
  public static readonly LEVEL = DIAMOND_TEST_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? DIAMOND_TEST_LEVEL : null;
  }
}
